import { useEffect, useMemo, useState, useCallback } from "react";
import { api, formatZAR, formatApiError } from "@/lib/api";
import { PageHeader, KpiCard } from "@/components/Kpi";
import { toast } from "sonner";
import { Search, Filter, X } from "lucide-react";

function todayMinusDays(n) {
    const d = new Date();
    d.setDate(d.getDate() - n);
    return d.toISOString().slice(0, 10);
}

export default function Transactions() {
    const [filters, setFilters] = useState({
        start: todayMinusDays(90),
        end: new Date().toISOString().slice(0, 10),
        type: "",
        search: "",
    });
    const [data, setData] = useState({ items: [], summary: [], count: 0, totals: {} });
    const [loading, setLoading] = useState(true);

    const fetchTx = useCallback(() => {
        setLoading(true);
        const q = new URLSearchParams();
        if (filters.start) q.set("start", filters.start);
        if (filters.end) q.set("end", filters.end);
        if (filters.type) q.set("transaction_type", filters.type);
        if (filters.search) q.set("search", filters.search);
        api.get(`/transactions?${q.toString()}`)
            .then((r) => setData(r.data))
            .catch((e) => toast.error(formatApiError(e)))
            .finally(() => setLoading(false));
    }, [filters]);

    useEffect(() => { fetchTx(); }, [fetchTx]);

    const sortedSummary = useMemo(() => [...(data.summary || [])].sort((a, b) => Math.abs(b.total) - Math.abs(a.total)), [data.summary]);
    const t = data.totals || {};

    const presets = [
        { label: "30d", start: todayMinusDays(30) },
        { label: "90d", start: todayMinusDays(90) },
        { label: "365d", start: todayMinusDays(365) },
        { label: "All", start: "2020-01-01" },
    ];

    return (
        <div>
            <PageHeader title="Transactions" subtitle={`${data.count} ${filters.type ? `'${filters.type}' ` : ""}entries matching your filter`}>
                <button data-testid="tx-clear-filters" onClick={() => setFilters({ start: "", end: "", type: "", search: "" })} className="text-xs text-[color:var(--bl-text-dim)] hover:text-[color:var(--bl-takealot-orange)] inline-flex items-center gap-1">
                    <X className="w-3 h-3" /> Clear
                </button>
            </PageHeader>

            <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4">
                <KpiCard label="Net total" value={formatZAR(t.amount)} testid="tx-total-amount" sub={`${t.count || 0} entries`} accent={t.amount >= 0 ? "green" : "orange"} />
                <KpiCard label="Credits" value={formatZAR(t.credits)} accent="green" testid="tx-total-credits" />
                <KpiCard label="Debits" value={formatZAR(t.debits)} accent="orange" testid="tx-total-debits" />
                <KpiCard label="Categories" value={sortedSummary.length} sub="distinct types" testid="tx-total-categories" />
            </div>

            <div className="bg-[color:var(--bl-surface)] border border-[color:var(--bl-border)] rounded-sm shadow-sm p-4 mb-4">
                <div className="flex items-center gap-2 mb-3 text-[color:var(--bl-text-dim)] text-xs uppercase tracking-[0.18em]">
                    <Filter className="w-3.5 h-3.5" /> Filters
                </div>
                <div className="grid grid-cols-2 md:grid-cols-5 gap-3 items-end">
                    <div>
                        <label className="block text-[10px] uppercase tracking-wider text-[color:var(--bl-text-dim)] mb-1">From</label>
                        <input data-testid="tx-filter-start" type="date" value={filters.start} onChange={(e) => setFilters({ ...filters, start: e.target.value })}
                            className="w-full bg-[color:var(--bl-bg)] border border-[color:var(--bl-border)] px-2 py-1.5 text-sm rounded-sm focus:outline-none focus:border-[color:var(--bl-takealot-blue)]" />
                    </div>
                    <div>
                        <label className="block text-[10px] uppercase tracking-wider text-[color:var(--bl-text-dim)] mb-1">To</label>
                        <input data-testid="tx-filter-end" type="date" value={filters.end} onChange={(e) => setFilters({ ...filters, end: e.target.value })}
                            className="w-full bg-[color:var(--bl-bg)] border border-[color:var(--bl-border)] px-2 py-1.5 text-sm rounded-sm focus:outline-none focus:border-[color:var(--bl-takealot-blue)]" />
                    </div>
                    <div>
                        <label className="block text-[10px] uppercase tracking-wider text-[color:var(--bl-text-dim)] mb-1">Type</label>
                        <select data-testid="tx-filter-type" value={filters.type} onChange={(e) => setFilters({ ...filters, type: e.target.value })}
                            className="w-full bg-[color:var(--bl-bg)] border border-[color:var(--bl-border)] px-2 py-1.5 text-sm rounded-sm focus:outline-none focus:border-[color:var(--bl-takealot-blue)]">
                            <option value="">All types</option>
                            {sortedSummary.map((s) => <option key={s.type} value={s.type}>{s.type.replace(/-/g, " ")}</option>)}
                        </select>
                    </div>
                    <div className="md:col-span-2">
                        <label className="block text-[10px] uppercase tracking-wider text-[color:var(--bl-text-dim)] mb-1">Search</label>
                        <div className="relative">
                            <Search className="w-3.5 h-3.5 absolute left-2 top-1/2 -translate-y-1/2 text-[color:var(--bl-text-dim)]" />
                            <input data-testid="tx-filter-search" placeholder="Order #, rule, reference…" value={filters.search} onChange={(e) => setFilters({ ...filters, search: e.target.value })}
                                className="w-full bg-[color:var(--bl-bg)] border border-[color:var(--bl-border)] pl-7 pr-2 py-1.5 text-sm rounded-sm focus:outline-none focus:border-[color:var(--bl-takealot-blue)]" />
                        </div>
                    </div>
                </div>
                <div className="flex items-center gap-2 mt-3">
                    <span className="text-[10px] uppercase tracking-wider text-[color:var(--bl-text-dim)]">Quick:</span>
                    {presets.map((p) => (
                        <button key={p.label} data-testid={`tx-preset-${p.label}`} onClick={() => setFilters({ ...filters, start: p.start, end: new Date().toISOString().slice(0, 10) })}
                            className="text-xs px-2 py-0.5 border border-[color:var(--bl-border)] rounded-sm hover:bg-[color:var(--bl-surface-2)]">{p.label}</button>
                    ))}
                </div>
            </div>

            <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4" data-testid="tx-summary">
                {sortedSummary.slice(0, 8).map((s) => (
                    <button key={s.type} data-testid={`tx-summary-${s.type}`} onClick={() => setFilters({ ...filters, type: filters.type === s.type ? "" : s.type })}
                        className={`text-left border bg-[color:var(--bl-surface)] p-3 rounded-sm hover:bg-[color:var(--bl-surface-2)]/50 transition shadow-sm ${
                            filters.type === s.type ? "border-[color:var(--bl-takealot-blue)]" : "border-[color:var(--bl-border)]"
                        }`}>
                        <div className="text-[10px] uppercase tracking-[0.18em] text-[color:var(--bl-text-dim)] truncate">{s.type.replace(/-/g, " ")}</div>
                        <div className={`mt-1 font-mono-num text-base ${s.total < 0 ? "text-[color:var(--bl-danger)]" : "text-[color:var(--bl-text)]"}`}>{formatZAR(s.total)}</div>
                        <div className="text-[10px] text-[color:var(--bl-text-dim)] mt-0.5">{s.count} entries</div>
                    </button>
                ))}
            </div>

            <div className="border border-[color:var(--bl-border)] bg-[color:var(--bl-surface)] rounded-sm overflow-x-auto shadow-sm">
                <table className="w-full text-sm min-w-[700px]">
                    <thead className="text-[10px] uppercase tracking-[0.18em] text-[color:var(--bl-text-dim)] border-b border-[color:var(--bl-border)] bg-[color:var(--bl-bg-2)]/40">
                        <tr>
                            <th className="text-left px-4 py-2">Date</th>
                            <th className="text-left px-3 py-2">Type</th>
                            <th className="text-right px-3 py-2">Amount (incl VAT)</th>
                            <th className="text-left px-3 py-2">Reference</th>
                        </tr>
                    </thead>
                    <tbody data-testid="tx-table">
                        {loading && (<tr><td colSpan={4} className="text-center text-[color:var(--bl-text-dim)] py-6">Loading…</td></tr>)}
                        {!loading && data.items.length === 0 && (<tr><td colSpan={4} className="text-center text-[color:var(--bl-text-dim)] py-6">No transactions match.</td></tr>)}
                        {data.items.slice(0, 2000).map((tx) => (
                            <tr key={tx.transaction_id} className="border-b border-[color:var(--bl-border)] hover:bg-[color:var(--bl-surface-2)]/50">
                                <td className="px-4 py-2 font-mono-num text-xs">{(tx.created_at || "").slice(0, 16).replace("T", " ")}</td>
                                <td className="px-3 py-2 text-xs">{tx.transaction_type?.replace(/-/g, " ")}</td>
                                <td className={`px-3 py-2 text-right font-mono-num ${(tx.amount_incl_vat || 0) < 0 ? "text-[color:var(--bl-danger)]" : "text-[color:var(--bl-success)]"}`}>{formatZAR(tx.amount_incl_vat)}</td>
                                <td className="px-3 py-2 text-xs text-[color:var(--bl-text-dim)]">{tx.references?.order_id ? `Order #${tx.references.order_id}` : tx.references?.rule || tx.references?.manual_reference || "—"}</td>
                            </tr>
                        ))}
                    </tbody>
                    {!loading && data.items.length > 0 && (
                        <tfoot className="bg-[color:var(--bl-bg-2)]/60 border-t-2 border-[color:var(--bl-border-strong)]">
                            <tr data-testid="tx-totals-row">
                                <td colSpan={2} className="px-4 py-2 text-xs uppercase tracking-wider text-[color:var(--bl-text-dim)]">Totals · {data.count} entries</td>
                                <td className={`px-3 py-2 text-right font-mono-num ${t.amount >= 0 ? "text-[color:var(--bl-success)]" : "text-[color:var(--bl-danger)]"}`}>{formatZAR(t.amount)}</td>
                                <td className="px-3 py-2"></td>
                            </tr>
                        </tfoot>
                    )}
                </table>
            </div>
        </div>
    );
}
